import React, { FC, useState } from 'react'
import { useAppSelector } from '../../store/hooks'
import {
  MainPage,
  PageTitle,
  FormDatePicker,
  PrimaryButton,
  ModalLoading,
  ConfirmModalStatus,
  Dropdown,
} from '../../components'
import { getShipmentGuideReport } from '../../services/ShipmentServices'
import moment from 'moment'
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import classNames from 'classnames'
import filePdfO from '@iconify/icons-fa/file-pdf-o'
import fileExcelO from '@iconify/icons-fa/file-excel-o'
import { Icon } from '@iconify/react'
import tealcaLogo from '../../assets/images/TealcaLogo.png'

const ShipmentGuideReport: FC = () => {
  const user = useAppSelector((state) => state.user)
  const [startDate, setStartDate] = useState<Date>(moment().toDate())
  const [endDate, setEndDate] = useState<Date>(moment().toDate())
  const [reportHtml, setReportHtml] = useState<string>('')

  const [loading, setLoading] = useState(false)
  const [loadingTitle, setLoadingTitle] = useState('')
  const [loadingStatus, setLoadingStatus] = useState<ConfirmModalStatus>(
    ConfirmModalStatus.PROCESSING
  )

  const handleSearch = async (format: 'HTML' | 'PDF' | 'EXCEL' = 'HTML') => {
    // Determine codCliente and rif from user store
    // user.client.accountCode seems to be codCliente
    // user.client.identificationNumber seems to be rif
    // Fallbacks to user properties if client not present (e.g. internal user?)
    // Based on ConsultShipmentsV2:
    const codCliente = user.client?.accountCode
      ? '' + user.client.accountCode
      : user.user?.number // Fallback hypothesis
        ? '' + user.user.number
        : ''

    const rif = user.client?.identificationNumber
      ? user.client.identificationNumber
      : user.user?.identificationNumber || ''

    if (!codCliente) {
      // Maybe show error in modal or alert
      // For now, continue, maybe backend handles empty
    }

    setLoading(true)
    setLoadingTitle(
      format === 'HTML' ? 'Generando reporte...' : 'Descargando reporte...'
    )
    setLoadingStatus(ConfirmModalStatus.PROCESSING)

    try {
      const response = await getShipmentGuideReport(
        codCliente,
        rif,
        moment(startDate).format('YYYY-MM-DD'),
        moment(endDate).format('YYYY-MM-DD'),
        format
      )

      if (format === 'HTML') {
        setReportHtml(response)
      } else {
        const url = window.URL.createObjectURL(new Blob([response]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `Reporte_Guias_${moment().format('YYYYMMDDHHmmss')}.${
            format === 'PDF' ? 'pdf' : 'xlsx'
          }`
        )
        document.body.appendChild(link)
        link.click()
        link.parentNode?.removeChild(link)
      }

      setLoadingStatus(ConfirmModalStatus.SUCCESS)
      setLoadingTitle('Proceso completado')
      setTimeout(() => setLoading(false), 500)
    } catch (error) {
      console.error(error)
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle('Error al procesar la solicitud')
      // Keep modal open for a bit so user sees error
      setTimeout(() => setLoading(false), 2000)
    }
  }

  return (
    <MainPage>
      <PageTitle title="Reporte de Guías" />

      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
          <div className="w-full">
            <FormDatePicker
              id="report-date-from"
              name="report-date-from"
              label="Fecha Desde"
              useRange={false}
              value={{
                startDate: startDate,
                endDate: startDate,
              }}
              onChange={(e) => {
                if (e?.startDate) {
                  setStartDate(moment(e.startDate).toDate())
                }
              }}
              toggleClassName={(oldClassname) =>
                classNames(oldClassname, 'text-indigo-600')
              }
            />
          </div>
          <div className="w-full">
            <FormDatePicker
              id="report-date-to"
              name="report-date-to"
              label="Fecha Hasta"
              useRange={false}
              value={{
                startDate: endDate,
                endDate: endDate,
              }}
              onChange={(e) => {
                if (e?.startDate) {
                  setEndDate(moment(e.startDate).toDate())
                }
              }}
              toggleClassName={(oldClassname) =>
                classNames(oldClassname, 'text-indigo-600')
              }
            />
          </div>
          <div className="w-full md:w-auto mb-0.5 flex gap-2">
            <PrimaryButton
              onClick={() => handleSearch('HTML')}
              disabled={loading}
            >
              <div className="flex items-center gap-2 justify-center">
                <MagnifyingGlassIcon className="w-5 h-5" />
                <span>Buscar</span>
              </div>
            </PrimaryButton>
          </div>
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow min-h-[300px] relative">
        {reportHtml && (
          <div className="flex justify-end p-2 border-b border-gray-100 mb-2">
            <div className="relative">
              <Dropdown
                label="Exportar"
                items={[
                  {
                    items: [
                      {
                        label: 'PDF',
                        icon: (props) => (
                          <Icon icon={filePdfO} className={props.className} />
                        ),
                        onClick: () => handleSearch('PDF'),
                      },
                      {
                        label: 'EXCEL',
                        icon: (props) => (
                          <Icon icon={fileExcelO} className={props.className} />
                        ),
                        onClick: () => handleSearch('EXCEL'),
                      },
                    ],
                  },
                ]}
                className=""
                buttonClassName="bg-main-500 hover:bg-main-400 text-white border-main-500 hover:border-main-400 !ring-0 h-[38px] items-center"
              />
            </div>
          </div>
        )}
        {reportHtml ? (
          <div
            dangerouslySetInnerHTML={{ __html: reportHtml }}
            className="overflow-auto"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
            <img
              src={tealcaLogo}
              alt="Tealca"
              className="w-32 opacity-20 mb-4"
            />
            <p>Seleccione rango de fechas y presione buscar</p>
          </div>
        )}
      </div>

      <ModalLoading
        open={loading}
        setOpen={setLoading}
        title={loadingTitle}
        status={loadingStatus}
      />
    </MainPage>
  )
}

export default ShipmentGuideReport
